import { Action } from '@anm/components/ActionsDropdown';
import { WAVE_EDITOR } from '@anm/constants/wave-editor';
import copyToClipboard from '@anm/helpers/copyToClipboard';
import { getDropdownAction, getDropdownActions } from '@anm/helpers/dropdownActions';
import usePageParams from '@anm/hooks/usePageParams';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { templateSelectors } from '../../../store/modules/template';
import { selectVideoFormats } from '../../../store/modules/video';
import OpenEditor from '../../OpenEditor';
import { VideoRatioItemProps } from '../VideoRatioList';

import { getTemplateId } from './helpers';

export const useVideoFormat = () => {
  const videoFormats = useSelector(selectVideoFormats);
  const currentTemplate = useSelector(templateSelectors.selectCurrentTemplate);

  if (!currentTemplate) {
    return false;
  }

  const currentFormat = videoFormats.find(format => format.name === currentTemplate.meta.defaultTarget)!.format;

  const [widthRatio, heightRatio] = currentFormat.split(':');
  const isVideoVertical = +heightRatio > +widthRatio;

  return isVideoVertical;
};

export const useActionDropdown = (openEditModal: () => void, currentRatioFormat?: VideoRatioItemProps) => {
  const currentTemplate = useSelector(templateSelectors.selectCurrentTemplate);
  const { embed, folderId } = usePageParams();
  const [actions, setActions] = useState([] as Action[]);

  const format = currentRatioFormat?.name || currentTemplate?.meta.defaultTarget;
  const params = {
    format,
    ...(embed && { embed }),
    ...(folderId && { folderId })
  };
  const templateId = currentTemplate?.templateId;

  const renderEditLink = (children: JSX.Element) => (
    <OpenEditor action="edit" templateId={getTemplateId(templateId)} {...{ params }}>
      {({ href }) => (
        <a href={href} target={embed ? '_self' : '_blank'}>
          {children}
        </a>
      )}
    </OpenEditor>
  );

  const editAction: Action = {
    ...getDropdownAction('onStaffEdit'),
    renderLink: renderEditLink
  };

  // TODO add download and template settings when discussed
  // this action is only for staff
  const newActions = [
    editAction,
    ...getDropdownActions({
      // onTemplateSettings: () => console.log('settings'),
      onStaffMetaEdit: openEditModal,
      ...(templateId && {
        onCopyProjectId: () => copyToClipboard(getTemplateId(templateId)!)
      })
      // onDownload: () => console.log('download')
    })
  ];

  useEffect(() => {
    setActions(newActions);
  }, [currentRatioFormat]);

  return actions;
};

export const useInternalEmbedCallback = (ratioFormatName: string, onTemplateEdit: () => void, templateId?: string) => {
  const internalEmbedCallback = useCallback(() => {
    if (!templateId) return;

    window.parent.postMessage(
      {
        target: WAVE_EDITOR,
        format: ratioFormatName,
        templateId: templateId.split('-')[0]
      },
      '*'
    );
    onTemplateEdit();
  }, [ratioFormatName, templateId]);

  return internalEmbedCallback;
};

export const useShareLink = () => {
  const [link, setLink] = useState('');

  useEffect(() => {
    setLink(window.location.href);
  }, []);

  return link;
};
